import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { useNavigate } from 'react-router';
import { MachineLogKpi } from '../../../shared/types/machineLogKpi.ts';
import { LinkButton } from '../../components/linkButton.tsx';
import { Batch } from '../../types/sharedTypeImpl.ts';
import { formatCycleTime, formatDatetime, formatEfficiency, formatPartsPerDay, formatPartsPerHour, formatPartsProduced, formatQuality, formatSetupTime } from '../../utils/fieldFormats.ts';
import { EquipmentOption, SelectedPart } from './types.ts';
import { useFetchBatches } from './useFetchBatches.ts';
import { useFetchBatchKpis } from './useFetchBatchKpis.ts';


export interface BatchesTableRef {
    refetchCurrentBatchKpis: () => void;
}

interface Props {
    selectedEquipment: EquipmentOption | null;
    selectedPart: SelectedPart | null;
    selectedBatch: Batch | null;
    setSelectedBatch: (batch: Batch | null) => void;
}

export const BatchesTable = forwardRef<BatchesTableRef, Props>(({
    selectedEquipment,
    selectedPart,
    selectedBatch,
    setSelectedBatch,
}: Props
    , ref) => {

    const { batches, hasMore, loadMore, isLoadingBatches } = useFetchBatches(selectedEquipment, selectedPart);
    const { kpiQueries, refetchCurrentBatchKpis } = useFetchBatchKpis({
        batches,
        selectedBatch,
        selectedEquipment,
        selectedPart,
    });

    //Select current batch on batches change
    useEffect(() => {
        if (!batches || batches.length == 0) {
            setSelectedBatch(null);
            return;
        }

        const isAlreadySelected = selectedBatch && batches.some(it => it === selectedBatch);
        if (isAlreadySelected) return;

        const sameBatch = selectedBatch &&
            batches.find(it => it.equipment === selectedBatch.equipment && +it.start === +selectedBatch.start);
        if (sameBatch) {
            setSelectedBatch(sameBatch);
        } else {
            const runningBatch = batches.find(it => it.equipment === selectedEquipment?.id && it.end === null) ?? null;
            setSelectedBatch(runningBatch);
        }
    }, [selectedBatch, selectedEquipment, batches, setSelectedBatch]);

    useImperativeHandle(ref, () => ({
        refetchCurrentBatchKpis
    }));


    function getGoodPartsString(kpis: MachineLogKpi | undefined): string {
        const partsCountAll = kpis?.kpisOverall?.goodPartsProduced;
        const partsCountDay = kpis?.kpisWorktime?.goodPartsProduced;
        if (partsCountAll == null) return '';

        const goodParts = formatPartsProduced(partsCountAll);

        const partsCountNight = partsCountDay != null ? partsCountAll - partsCountDay : null;
        const percentageNight = partsCountNight != null && partsCountAll ? partsCountNight / partsCountAll : null;
        const percentageNightString = percentageNight != null ? `${Math.round(percentageNight * 100)}%` : null;

        const out = goodParts + (percentageNightString ? ` (${percentageNightString})` : '');
        return out;
    }


    const targetColumn = selectedEquipment ? 'partNumber' : 'equipment';

    if (batches == null) return null;
    if (batches.length == 0) return (
        <p>
            No batches found
            {selectedPart?.partName && ' (Note: Part names should not contain the revision)'}
        </p>
    );
    return <>
        <table style={{ marginTop: '16px' }}>
            <thead>
                <tr>
                    <th>Rev</th>
                    <th>Start</th>
                    <th>End</th>
                    <th>{targetColumn === 'equipment' ? 'Equipment' : 'Part number'}</th>
                    <th title='Median cycle time after setup&#10;Latest 8 hours shown in parentheses'>
                        Cycle time
                    </th>
                    <th
                        title='efficiency = median cycle time * good parts count / production time&#10;Latest 8 hours shown in parentheses'>
                        Efficiency
                    </th>
                    <th
                        title='parts/hour = good parts count / production days after setup&#10;The rate of the latest 8 hours shown in parentheses.'>
                        Parts / hour
                    </th>
                    <th
                        title='parts/day = good parts count / production days after setup&#10;The rate of the latest 8 hours shown in parentheses.'>
                        Parts / day
                    </th>
                    <th title='quality = good parts / all parts&#10;Latest 8 hours shown in parentheses.'>
                        Quality
                    </th>
                    <th title='good parts = actual parts produced - scraps&#10;The percentage of parts that were produced during off hours is shown in parentheses.'>
                        Good parts
                    </th>
                    <th>Setup time</th>
                </tr>
            </thead>

            <tbody>
                {
                    batches?.map((batch, index) => {
                        const isLoading = kpiQueries[index].isLoading;
                        const kpis = kpiQueries[index].data;

                        return (
                            <tr
                                key={batch.equipment + +batch.start}
                                className={selectedBatch === batch ? 'highlight--selected' : ''}
                                onClick={() => setSelectedBatch(batch)}
                            >
                                <td>{batch.revision ?? ''}</td>
                                <td>{formatDatetime(batch.start)}</td>
                                <td>{formatDatetime(batch.end)}</td>
                                <td style={{ textAlign: 'left', paddingLeft: '20px' }}>
                                    <TargetColumn batch={batch} targetColumn={targetColumn} />
                                </td>
                                <td>{isLoading ? '...'
                                    : <div>
                                        <div>{formatCycleTime(kpis?.kpisOverall?.cycleTimeMedian)}</div>
                                        {kpis?.kpisRecent && <div>({formatCycleTime(kpis?.kpisRecent?.cycleTimeMedian)})</div>}
                                    </div>
                                }</td>
                                <td>{isLoading ? '...'
                                    : <div>
                                        <div>{formatEfficiency(kpis?.kpisOverall?.efficiency)}</div>
                                        {kpis?.kpisRecent && <div>({formatEfficiency(kpis?.kpisRecent?.efficiency)})</div>}
                                    </div>
                                }</td>
                                <td>{isLoading ? '...'
                                    : <div>
                                        <div>{formatPartsPerHour(kpis?.kpisOverall?.goodPartsPerHour)}</div>
                                        {kpis?.kpisRecent && <div>({formatPartsPerHour(kpis?.kpisRecent?.goodPartsPerHour)})</div>}
                                    </div>
                                }</td>
                                <td>{isLoading ? '...'
                                    : <div>
                                        <div>{formatPartsPerDay(kpis?.kpisOverall?.goodPartsPerHour)}</div>
                                        {kpis?.kpisRecent && <div>({formatPartsPerDay(kpis?.kpisRecent?.goodPartsPerHour)})</div>}
                                    </div>
                                }</td>
                                <td>{isLoading ? '...'
                                    : <div>
                                        <div>{formatQuality(kpis?.kpisOverall?.quality)}</div>
                                        {kpis?.kpisRecent && <div>({formatQuality(kpis?.kpisRecent?.quality)})</div>}
                                    </div>
                                }</td>
                                <td>{isLoading ? '...' : getGoodPartsString(kpis)}</td>
                                <td>{isLoading ? '...' : formatSetupTime(kpis?.activeSetupTime)}</td>
                            </tr>
                        )
                    })
                }
            </tbody>
        </table>
        {batches && (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
                <LinkButton
                    children={isLoadingBatches ? 'Loading...' : 'Load more'}
                    disabled={isLoadingBatches || !hasMore}
                    onClick={loadMore}
                />
            </div>
        )}
    </>;
});


function TargetColumn({
    batch,
    targetColumn,
}: {
    batch: Batch;
    targetColumn: 'partNumber' | 'equipment';
}) {
    const navigate = useNavigate();

    const text = targetColumn === 'equipment' ? batch.equipmentDisplayName
        : batch.partDisplayName;
    const link = targetColumn === 'equipment' ? `/machine-log#equipment=${batch.equipment}`
        : batch?.partName ? `/machine-log#partName=${batch.partName}`
            : `/machine-log#product=${batch.product}`;

    return (
        <span
            style={{ textDecoration: 'underline', cursor: 'pointer' }}
            onClick={() => navigate(link)}
        >
            {text}
        </span>
    );
}
